import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import '../Dropdown/Dropdown.css';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Colunas exatas que o template deve conter (na ordem)
const COLUNAS_TEMPLATE = [
  'Nome',
  'CPF_CNPJ',
  'Celular',
  'Email',
  'Endereco',
  'Numero',
  'Bairro',
  'Cidade',
  'UF',
  'CEP',
  'RG',
  'DataNascimento',
  'TipoPessoa',
  'RegUnico'
];

// Campos obrigatórios
const CAMPOS_OBRIGATORIOS = ['Nome', 'CPF_CNPJ', 'Celular'];

// apiCall helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  for (const url of urls) {
    try {
      const options = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };
      const token = getAuthToken();
      if (token) options.headers['Authorization'] = `Bearer ${token}`;
      if (data && method !== 'GET') options.body = JSON.stringify(data);
      const response = await fetch(url, options);
      if (response.ok) {
        return method === 'GET' ? await response.json() : response;
      }
    } catch (_) {}
  }
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}.`);
};

export default function PopupImportacaoClientesEmMassa({ onFechar, onSucesso }) {
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
  const [resultados, setResultados] = useState([]);
  const [erroArquivo, setErroArquivo] = useState('');
  const [concluido, setConcluido] = useState(false);
  const inputRef = useRef(null);
  const abortadoRef = useRef(false);

  // ─── Gerar e baixar template XLSX ────────────────────────────────────────────
  const baixarTemplate = () => {
    const EXEMPLOS = [
      {
        Nome: 'João Silva',
        CPF_CNPJ: '12345678900',
        Celular: '11999998888',
        Email: 'joao@email.com',
        Endereco: 'Rua das Flores',
        Numero: '123',
        Bairro: 'Centro',
        Cidade: 'São Paulo',
        UF: 'SP',
        CEP: '01234567',
        RG: '123456789',
        DataNascimento: '15/01/1985',
        TipoPessoa: 'Pessoa Física',
        RegUnico: ''
      },
      {
        Nome: 'Maria Oliveira',
        CPF_CNPJ: '12345678000190',
        Celular: '11888887777',
        Email: 'maria@empresa.com',
        Endereco: 'Av. Paulista',
        Numero: '1000',
        Bairro: 'Bela Vista',
        Cidade: 'São Paulo',
        UF: 'SP',
        CEP: '01310100',
        RG: '',
        DataNascimento: '22/03/1990',
        TipoPessoa: 'Pessoa Jurídica',
        RegUnico: ''
      }
    ];

    const ws = XLSX.utils.json_to_sheet(EXEMPLOS, { header: COLUNAS_TEMPLATE });

    // Estilo de largura das colunas
    ws['!cols'] = COLUNAS_TEMPLATE.map(col => ({ wch: Math.max(col.length + 4, 18) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');

    // Segunda aba com instruções
    const instrucoes = [
      { Campo: 'Nome', Obrigatorio: 'SIM', Descricao: 'Nome completo do cliente' },
      { Campo: 'CPF_CNPJ', Obrigatorio: 'SIM', Descricao: 'CPF (11 dígitos) ou CNPJ (14 dígitos), somente números' },
      { Campo: 'Celular', Obrigatorio: 'SIM', Descricao: 'Número de celular/telefone, somente números' },
      { Campo: 'Email', Obrigatorio: 'Não', Descricao: 'E-mail de contato' },
      { Campo: 'Endereco', Obrigatorio: 'Não', Descricao: 'Logradouro (rua, avenida, etc.)' },
      { Campo: 'Numero', Obrigatorio: 'Não', Descricao: 'Número do endereço' },
      { Campo: 'Bairro', Obrigatorio: 'Não', Descricao: 'Bairro' },
      { Campo: 'Cidade', Obrigatorio: 'Não', Descricao: 'Cidade' },
      { Campo: 'UF', Obrigatorio: 'Não', Descricao: 'Estado (sigla, ex: SP)' },
      { Campo: 'CEP', Obrigatorio: 'Não', Descricao: 'CEP somente números' },
      { Campo: 'RG', Obrigatorio: 'Não', Descricao: 'RG (apenas Pessoa Física)' },
      { Campo: 'DataNascimento', Obrigatorio: 'Não', Descricao: 'Data no formato DD/MM/AAAA' },
      { Campo: 'TipoPessoa', Obrigatorio: 'Não', Descricao: 'Pessoa Física | Pessoa Jurídica | MEI | Autônomo | Sociedade Ltda' },
      { Campo: 'RegUnico', Obrigatorio: 'Não', Descricao: 'Registro único interno (opcional)' },
    ];
    const wsInst = XLSX.utils.json_to_sheet(instrucoes, { header: ['Campo', 'Obrigatorio', 'Descricao'] });
    wsInst['!cols'] = [{ wch: 18 }, { wch: 12 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInst, 'Instruções');

    XLSX.writeFile(wb, 'template_importacao_clientes.xlsx');
  };

  // ─── Validar cabeçalho do arquivo ─────────────────────────────────────────────
  const validarColunas = (colunas) => {
    const faltando = COLUNAS_TEMPLATE.filter(c => !colunas.includes(c));
    const extras = colunas.filter(c => !COLUNAS_TEMPLATE.includes(c));
    return { faltando, extras };
  };

  // ─── Converter data DD/MM/AAAA → ISO ─────────────────────────────────────────
  const converterData = (valor) => {
    if (!valor) return null;
    const str = String(valor).trim();
    // Formato DD/MM/AAAA
    const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
    // Já em ISO
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.substring(0, 10);
    return null;
  };

  // ─── Mapear TipoPessoa texto → ID ────────────────────────────────────────────
  const mapTipoPessoa = (texto) => {
    if (!texto) return null;
    const mapa = {
      'pessoa física': 1, 'pessoa fisica': 1,
      'pessoa jurídica': 2, 'pessoa juridica': 2,
      'mei': 3,
      'autônomo': 4, 'autonomo': 4,
      'sociedade ltda': 5
    };
    return mapa[String(texto).toLowerCase().trim()] || null;
  };

  // ─── Processar upload ─────────────────────────────────────────────────────────
  const handleArquivo = (e) => {
    const f = e.target.files && e.target.files[0];
    setErroArquivo('');
    setResultados([]);
    setConcluido(false);
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setErroArquivo('Formato inválido. Utilize somente arquivos .xlsx ou .xls do template padrão do sistema.');
      setArquivo(null);
      return;
    }
    setArquivo(f);
  };

  const iniciarImportacao = async () => {
    if (!arquivo) return;
    setErroArquivo('');
    setResultados([]);
    setConcluido(false);
    abortadoRef.current = false;

    // Ler arquivo
    let linhas;
    try {
      const buffer = await arquivo.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      linhas = XLSX.utils.sheet_to_json(ws, { defval: '' });
    } catch {
      setErroArquivo('Não foi possível ler o arquivo. Verifique se é um arquivo Excel válido.');
      return;
    }

    if (!linhas || linhas.length === 0) {
      setErroArquivo('O arquivo não contém dados. Verifique se preencheu as linhas na planilha.');
      return;
    }

    // Validar colunas
    const colunasArquivo = Object.keys(linhas[0]);
    const { faltando } = validarColunas(colunasArquivo);
    if (faltando.length > 0) {
      setErroArquivo(
        `As colunas abaixo estão faltando no arquivo. Utilize apenas o template padrão do sistema para importação:\n• ${faltando.join('\n• ')}`
      );
      return;
    }

    // Processar linha a linha
    setProcessando(true);
    setProgresso({ atual: 0, total: linhas.length });
    const logs = [];

    for (let i = 0; i < linhas.length; i++) {
      if (abortadoRef.current) break;
      const linha = linhas[i];
      const numLinha = i + 1;

      setProgresso({ atual: i + 1, total: linhas.length });

      // Validar campos obrigatórios
      const errosObrig = CAMPOS_OBRIGATORIOS.filter(c => !String(linha[c] || '').trim());
      if (errosObrig.length > 0) {
        logs.push({
          linha: numLinha,
          nome: linha.Nome || `Linha ${numLinha}`,
          status: 'erro',
          mensagem: `Campos obrigatórios ausentes: ${errosObrig.join(', ')}`
        });
        continue;
      }

      // Montar payload
      const payload = {
        nome: String(linha.Nome).trim(),
        cpF_CNPJ: String(linha.CPF_CNPJ).trim().replace(/\D/g, ''),
        celular: String(linha.Celular).trim(),
        email: String(linha.Email || '').trim(),
        endereco: String(linha.Endereco || '').trim(),
        numero: String(linha.Numero || '').trim(),
        bairro: String(linha.Bairro || '').trim(),
        cidade: String(linha.Cidade || '').trim(),
        uf: String(linha.UF || '').trim().toUpperCase(),
        cep: String(linha.CEP || '').trim().replace(/\D/g, ''),
        rg: String(linha.RG || '').trim(),
        data_nasc: converterData(linha.DataNascimento),
        tipoPessoaId: mapTipoPessoa(linha.TipoPessoa),
        regUnico: String(linha.RegUnico || '').trim()
      };

      try {
        const resp = await apiCall('Cliente/CriarCliente', 'POST', payload);
        if (resp && (resp.ok || resp.status === 200 || resp.status === 201)) {
          logs.push({ linha: numLinha, nome: payload.nome, status: 'sucesso', mensagem: 'Cadastrado com sucesso' });
        } else {
          const txt = typeof resp.text === 'function' ? await resp.text() : 'Erro desconhecido';
          logs.push({ linha: numLinha, nome: payload.nome, status: 'erro', mensagem: txt || 'Erro ao cadastrar' });
        }
      } catch (err) {
        logs.push({ linha: numLinha, nome: payload.nome, status: 'erro', mensagem: err.message });
      }

      setResultados([...logs]);

      // Pequeno delay para não travar a UI
      await new Promise(r => setTimeout(r, 80));
    }

    setProcessando(false);
    setConcluido(true);
    if (onSucesso) onSucesso();
  };

  const cancelar = () => {
    abortadoRef.current = true;
  };

  const sucessos = resultados.filter(r => r.status === 'sucesso').length;
  const erros = resultados.filter(r => r.status === 'erro').length;
  const pct = progresso.total > 0 ? Math.round((progresso.atual / progresso.total) * 100) : 0;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 95000
      }}
      onClick={processando ? undefined : onFechar}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, width: 700, maxWidth: '96vw',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.28)', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Cabeçalho ─────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
          padding: '18px 24px', borderRadius: '10px 10px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 700 }}>
              📥 Cadastro em Massa de Clientes
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', margin: '4px 0 0', fontSize: 13 }}>
              Importe vários clientes de uma vez usando a planilha Excel padrão do sistema
            </p>
          </div>
          {!processando && (
            <button
              type="button"
              onClick={onFechar}
              aria-label="Fechar popup"
              style={{
                background: 'transparent', border: 'none', color: '#fff',
                padding: 0, width: 'auto', height: 'auto', fontSize: 24,
                cursor: 'pointer', fontWeight: 700, lineHeight: 1
              }}
            >×</button>
          )}
        </div>

        <div style={{ padding: '22px 26px' }}>

          {/* ── Instruções ───────────────────────────────────────── */}
          <div style={{
            background: '#fff8e1', border: '1.5px solid #ffe082',
            borderRadius: 8, padding: '14px 18px', marginBottom: 22
          }}>
            <p style={{ fontWeight: 700, color: 'rgb(184, 98, 0)', marginBottom: 8, fontSize: 14 }}>
              📋 Como funciona o Cadastro em Massa?
            </p>
            <ol style={{ margin: 0, paddingLeft: 20, color: '#5d4037', fontSize: 13, lineHeight: 1.7 }}>
              <li>Clique em <strong>"Baixar Template Excel"</strong> para obter a planilha padrão do sistema.</li>
              <li>Abra a planilha e preencha os dados dos clientes — <strong>uma linha por cliente</strong>.</li>
              <li>Os campos <strong>Nome, CPF/CNPJ e Celular</strong> são <strong>obrigatórios</strong>. Linhas com campos obrigatórios ausentes serão <strong>ignoradas</strong>, mas o restante continuará sendo processado.</li>
              <li>Salve o arquivo e faça o upload abaixo.</li>
              <li><strong>Atenção:</strong> somente o template padrão do sistema é aceito. Arquivos com colunas diferentes serão rejeitados.</li>
            </ol>
          </div>

          {/* ── Download template ─────────────────────────────────── */}
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <button
              onClick={baixarTemplate}
              disabled={processando}
              style={{
                background: 'linear-gradient(135deg, #43a047, #2e7d32)',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '11px 28px', fontSize: 14, fontWeight: 700,
                cursor: processando ? 'not-allowed' : 'pointer',
                boxShadow: '0 3px 10px rgba(67,160,71,0.3)',
                display: 'inline-flex', alignItems: 'center', gap: 8
              }}
            >
              ⬇️ Baixar Template Excel
            </button>
            <p style={{ color: '#888', fontSize: 12, marginTop: 6 }}>
              Contém todas as colunas necessárias e exemplos de preenchimento
            </p>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', marginBottom: 20 }} />

          {/* ── Upload ───────────────────────────────────────────── */}
          {!processando && !concluido && (
            <div>
              <label style={{ fontWeight: 700, color: '#333', fontSize: 14, display: 'block', marginBottom: 8 }}>
                Selecionar Planilha Excel (.xlsx / .xls)
              </label>
              <div
                style={{
                  border: '2px dashed rgb(235, 133, 0)', borderRadius: 8,
                  padding: '28px 20px', textAlign: 'center',
                  background: arquivo ? '#fff8e1' : '#fafafa',
                  cursor: 'pointer', transition: 'all .2s'
                }}
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
                <p style={{ color: '#616161', margin: 0, fontSize: 14 }}>
                  {arquivo
                    ? <>✅ <strong>{arquivo.name}</strong></>
                    : 'Clique aqui ou arraste a planilha preenchida'}
                </p>
                <p style={{ color: '#aaa', margin: '4px 0 0', fontSize: 12 }}>
                  Somente arquivos .xlsx ou .xls gerados pelo template padrão
                </p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleArquivo}
              />

              {erroArquivo && (
                <div style={{
                  background: '#ffebee', border: '1.5px solid #ef9a9a',
                  borderRadius: 8, padding: '12px 16px', marginTop: 14,
                  color: '#c62828', fontSize: 13, whiteSpace: 'pre-line'
                }}>
                  ❌ {erroArquivo}
                </div>
              )}

              {arquivo && !erroArquivo && (
                <div style={{ textAlign: 'center', marginTop: 18 }}>
                  <button
                    onClick={iniciarImportacao}
                    style={{
                      background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                      color: '#fff', border: 'none', borderRadius: 8,
                      padding: '12px 36px', fontSize: 15, fontWeight: 700,
                      cursor: 'pointer', boxShadow: '0 3px 10px rgba(235, 133, 0,0.35)'
                    }}
                  >
                    🚀 Iniciar Importação
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Barra de progresso ────────────────────────────────── */}
          {(processando || concluido) && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, color: '#333', fontSize: 14 }}>
                  {processando ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-block', width: 18, height: 18,
                        border: '3px solid rgb(235, 133, 0)', borderTop: '3px solid transparent',
                        borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                      }} />
                      Processando clientes...
                    </span>
                  ) : '✅ Importação concluída!'}
                </span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {progresso.atual} de {progresso.total} — {pct}%
                </span>
              </div>
              <div style={{ background: '#e0e0e0', borderRadius: 20, height: 12, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 20,
                  background: concluido ? '#43a047' : 'linear-gradient(90deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                  width: `${pct}%`, transition: 'width 0.3s'
                }} />
              </div>

              {concluido && (
                <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                  <div style={{
                    flex: 1, background: '#e8f5e9', borderRadius: 8, padding: '10px 14px',
                    textAlign: 'center', borderLeft: '4px solid #43a047'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#2e7d32' }}>{sucessos}</div>
                    <div style={{ color: '#388e3c', fontSize: 12 }}>Cadastrados com sucesso</div>
                  </div>
                  <div style={{
                    flex: 1, background: '#ffebee', borderRadius: 8, padding: '10px 14px',
                    textAlign: 'center', borderLeft: '4px solid #e53935'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#c62828' }}>{erros}</div>
                    <div style={{ color: '#e53935', fontSize: 12 }}>Linhas com erro / ignoradas</div>
                  </div>
                </div>
              )}

              {processando && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button
                    onClick={cancelar}
                    style={{
                      background: '#fff', border: '1.5px solid #e53935', color: '#e53935',
                      borderRadius: 6, padding: '7px 20px', fontSize: 13, cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Log de resultados ─────────────────────────────────── */}
          {resultados.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontWeight: 700, color: '#333', fontSize: 14, marginBottom: 8 }}>
                📝 Log detalhado
              </p>
              <div style={{
                maxHeight: 240, overflowY: 'auto', border: '1px solid #e0e0e0',
                borderRadius: 8, fontSize: 12
              }}>
                {resultados.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '7px 12px',
                      background: r.status === 'sucesso' ? (i % 2 === 0 ? '#f9fff9' : '#f1fdf1') : (i % 2 === 0 ? '#fff5f5' : '#ffefef'),
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex', alignItems: 'flex-start', gap: 8
                    }}
                  >
                    <span style={{ minWidth: 20 }}>{r.status === 'sucesso' ? '✅' : '❌'}</span>
                    <span style={{ minWidth: 70, color: '#888' }}>Linha {r.linha}</span>
                    <span style={{ minWidth: 180, fontWeight: 600, color: '#333' }}>{r.nome}</span>
                    <span style={{ color: r.status === 'sucesso' ? '#388e3c' : '#c62828' }}>{r.mensagem}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Botões finais ─────────────────────────────────────── */}
          {concluido && (
            <div style={{ textAlign: 'center', marginTop: 20, display: 'flex', justifyContent: 'center', gap: 14 }}>
              <button
                onClick={() => {
                  setArquivo(null);
                  setResultados([]);
                  setConcluido(false);
                  setProgresso({ atual: 0, total: 0 });
                  setErroArquivo('');
                  if (inputRef.current) inputRef.current.value = '';
                }}
                style={{
                  background: '#fff', border: '1.5px solid rgb(235, 133, 0)', color: 'rgb(235, 133, 0)',
                  borderRadius: 8, padding: '10px 24px', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Nova Importação
              </button>
              <button
                onClick={onFechar}
                style={{
                  background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 28px', fontSize: 14,
                  fontWeight: 700, cursor: 'pointer'
                }}
              >
                Fechar
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Keyframes para spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
